import * as v from 'valibot';

/**
 * The skills registry: what a skill is, how it is checked, and how often it ran.
 *
 * Reef already had skills — versioned directories under `/skills`, attached to
 * steps by name. What it did not have is the thing that makes a hundred of them
 * survivable: a lint that runs on every one of them in CI, a count of how often
 * each is actually used, and a failure rate its author can read. Without those,
 * a registry becomes a graveyard within a quarter, because nobody can tell the
 * skill that governs half the fleet from the one somebody wrote once and
 * abandoned.
 *
 * Nothing here stores instructions. The skill body lives in its `SKILL.md`,
 * committed and reviewable in a diff, and everything in this file is either
 * derived from that file or measured from runs of it.
 */

/**
 * How much a lint finding matters.
 *
 * Two levels, not five. `error` fails CI and `warning` does not, and every
 * severity beyond those two is a level nobody agrees the meaning of — which in
 * practice means the middle ones are ignored and the check stops being read.
 */
export const SkillLintSeverity = v.picklist(['error', 'warning']);
export type SkillLintSeverity = v.InferOutput<typeof SkillLintSeverity>;

export const SkillLintFinding = v.object({
  /** Stable rule id, e.g. `missing-frontmatter`. Quoted in CI output, so it never changes. */
  rule: v.string(),
  severity: SkillLintSeverity,
  message: v.string(),
  /** The line the finding is about, when the rule is about a line. */
  line: v.optional(v.nullable(v.pipe(v.number(), v.integer())), null),
});
export type SkillLintFinding = v.InferOutput<typeof SkillLintFinding>;

/**
 * A skill as the registry lists it.
 *
 * `version` and `family` are parsed from the directory name rather than stored:
 * `code-standards-v2` is version 2 of the `code-standards` family, and a
 * separate field saying so is a second source of truth that goes stale the first
 * time somebody renames a directory.
 */
export const SkillManifest = v.object({
  name: v.string(),
  family: v.string(),
  version: v.pipe(v.number(), v.integer(), v.minValue(1)),
  /** The one-liner from the frontmatter — what discovery matches against. */
  description: v.string(),
  /** Bytes of instruction. A model reads all of it, every turn, on every step that mounts it. */
  bytes: v.pipe(v.number(), v.integer()),
  findings: v.optional(v.array(SkillLintFinding), []),
  /** Workflow ids with at least one step that mounts this skill. */
  usedBy: v.optional(v.array(v.string()), []),
  /** Steps across all workflows that mount it. Zero means nothing runs it. */
  stepCount: v.optional(v.pipe(v.number(), v.integer()), 0),
  /** True when a newer version of the same family exists. */
  superseded: v.optional(v.boolean(), false),
});
export type SkillManifest = v.InferOutput<typeof SkillManifest>;

/**
 * What running a skill did, one row per step run that mounted it.
 *
 * Recorded per *step*, not per model call: a step is the unit whose outcome a
 * skill author can act on. "This skill was in context for four hundred model
 * turns" tells them nothing; "the eleven steps that mounted it last week failed
 * twice" tells them where to look.
 */
export const SkillRunOutcome = v.picklist(['completed', 'failed', 'running']);
export type SkillRunOutcome = v.InferOutput<typeof SkillRunOutcome>;

export const SkillUsage = v.object({
  skill: v.string(),
  windowHours: v.number(),
  runs: v.pipe(v.number(), v.integer()),
  failures: v.pipe(v.number(), v.integer()),
  /** Failures over runs that ended. Null while nothing has ended — not zero. */
  failureRate: v.nullable(v.number()),
  /** Workflows that ran it in the window, most frequent first. */
  workflows: v.optional(v.array(v.object({ id: v.string(), runs: v.number() })), []),
  lastRunAt: v.nullable(v.string()),
});
export type SkillUsage = v.InferOutput<typeof SkillUsage>;

/**
 * What `install` answers: the skills a step in this context should mount.
 *
 * The defaults are the whole feature. An engineer who has to choose from two
 * and a half thousand skills chooses none, so the registry answers "given that
 * you are writing a step that touches this repository, on this workflow, these
 * are the ones that apply" and the caller accepts or narrows it.
 */
export const SkillInstallSuggestion = v.object({
  skill: v.string(),
  /** Why this one, in the words the studio shows next to the checkbox. */
  reason: v.string(),
  /** True when it is a default rather than a match — installed unless removed. */
  standard: v.boolean(),
});
export type SkillInstallSuggestion = v.InferOutput<typeof SkillInstallSuggestion>;

/**
 * The lint.
 *
 * Every rule here is one that has a *consequence at run time*, which is the bar
 * for adding one. A missing description means discovery cannot match the skill
 * and nobody installs it; a body of forty kilobytes means every step that
 * mounts it pays for forty kilobytes on every turn; a hard-coded secret in an
 * instruction file is a hard-coded secret in a git history. Style is not linted,
 * because a lint that fires on things nobody would fix is a lint people switch
 * off.
 */
export const SKILL_LINT_RULES = [
  { rule: 'missing-frontmatter', severity: 'error' as const },
  { rule: 'missing-name', severity: 'error' as const },
  { rule: 'name-mismatch', severity: 'error' as const },
  { rule: 'missing-description', severity: 'error' as const },
  { rule: 'description-too-long', severity: 'warning' as const },
  { rule: 'unversioned-name', severity: 'error' as const },
  { rule: 'empty-body', severity: 'error' as const },
  { rule: 'body-too-large', severity: 'warning' as const },
  { rule: 'no-headings', severity: 'warning' as const },
  { rule: 'hardcoded-secret', severity: 'error' as const },
  { rule: 'absolute-local-path', severity: 'warning' as const },
] as const;

/**
 * Frontmatter description, in characters.
 *
 * Set against the convention the corpus actually follows — what the skill does,
 * then when to use it, which is two sentences and lands around 250 characters.
 * A limit tuned to a single line would fire on most of the registry on day one,
 * and a lint that warns about the house style is a lint people filter out. What
 * this catches is the other failure: a description that is really the body,
 * pasted into the one field discovery ranks on.
 */
export const SKILL_DESCRIPTION_MAX = 320;
/**
 * Instruction bytes past which a skill is flagged.
 *
 * 12 KB is roughly three thousand tokens carried by every turn of every step
 * that mounts the skill. Past that it is usually two skills that want splitting,
 * and the split is what makes either of them individually evaluable.
 */
export const SKILL_BODY_MAX_BYTES = 12_000;

/** `code-standards-v2` → `{ family: 'code-standards', version: 2 }`. */
export function parseSkillName(name: string): { family: string; version: number } | null {
  const match = /^(.*)-v(\d+)$/.exec(name);
  if (!match) return null;
  return { family: match[1]!, version: Number.parseInt(match[2]!, 10) };
}

/**
 * Lint one `SKILL.md`, from its directory name and its raw source.
 *
 * Pure, and here rather than in the Worker, because two things run it: the
 * registry endpoint that shows an author their findings, and the CI script that
 * fails a pull request. Two implementations of the same rules is how a skill
 * passes locally and fails in CI, which is the fastest way to get a check
 * disabled.
 *
 * Frontmatter is parsed by hand rather than with a YAML library. The three keys
 * that matter are scalars on their own line, and taking a YAML dependency into
 * the schema package — which the workflow build and the Worker both load —
 * costs more than it buys.
 */
export function lintSkillDocument(directoryName: string, source: string): SkillLintFinding[] {
  const findings: SkillLintFinding[] = [];
  const add = (rule: string, message: string, line: number | null = null) => {
    const known = SKILL_LINT_RULES.find((entry) => entry.rule === rule);
    findings.push({ rule, severity: known?.severity ?? 'warning', message, line });
  };

  if (!parseSkillName(directoryName)) {
    add(
      'unversioned-name',
      `"${directoryName}" has no version suffix. A skill directory is \`<family>-v<n>\`, so a step can pin the version it was reviewed against.`,
    );
  }

  const frontmatter = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(source);
  if (!frontmatter) {
    add('missing-frontmatter', 'the file has no YAML frontmatter, so the loader cannot read its name or description.', 1);
    return findings;
  }

  const block = frontmatter[1]!;
  const body = source.slice(frontmatter[0].length).trim();
  const name = scalar(block, 'name');
  const description = scalar(block, 'description');

  if (!name) {
    add('missing-name', 'frontmatter has no `name`.', 2);
  } else if (name !== directoryName) {
    add(
      'name-mismatch',
      `frontmatter says \`${name}\` but the directory is \`${directoryName}\`. Steps reference the directory, so the two must agree.`,
      2,
    );
  }

  if (!description) {
    add(
      'missing-description',
      'frontmatter has no `description`. Discovery matches on it, so a skill without one is a skill nobody installs.',
      2,
    );
  } else if (description.length > SKILL_DESCRIPTION_MAX) {
    add(
      'description-too-long',
      `the description is ${description.length} characters; keep it under ${SKILL_DESCRIPTION_MAX} so the picker shows all of it.`,
      2,
    );
  }

  if (body.length === 0) {
    add('empty-body', 'the file has frontmatter and no instructions.');
    return findings;
  }

  const bytes = new TextEncoder().encode(body).length;
  if (bytes > SKILL_BODY_MAX_BYTES) {
    add(
      'body-too-large',
      `${bytes} bytes of instruction is carried by every turn of every step that mounts this. Over ${SKILL_BODY_MAX_BYTES} it is usually two skills that want splitting.`,
    );
  }

  if (!/^#{1,3}\s+\S/m.test(body)) {
    add('no-headings', 'the body has no headings, which makes it hard for a reader to check one rule without reading all of them.');
  }

  const secret =
    /\b(?:sk-[A-Za-z0-9_-]{16,}|ghp_[A-Za-z0-9]{20,}|xox[baprs]-[A-Za-z0-9-]{10,}|AKIA[0-9A-Z]{16})\b|-----BEGIN [A-Z ]*PRIVATE KEY-----/.exec(
      body,
    );
  if (secret) {
    add(
      'hardcoded-secret',
      'the instructions contain something shaped like a live credential. A skill is committed, so this is a credential in a git history.',
      lineOf(source, secret.index),
    );
  }

  const localPath = /(?:^|\s)(\/(?:Users|home)\/[^\s`'"]+)/.exec(body);
  if (localPath) {
    add(
      'absolute-local-path',
      `\`${localPath[1]}\` is a path on somebody's laptop. Steps run in a container or an isolate, where it does not exist.`,
      lineOf(source, localPath.index),
    );
  }

  return findings;
}

/** One `key: value` line out of a frontmatter block, unquoted. */
function scalar(block: string, key: string): string | null {
  const match = new RegExp(`^${key}\\s*:\\s*(.+)$`, 'm').exec(block);
  if (!match) return null;
  return match[1]!.trim().replace(/^['"]|['"]$/g, '');
}

function lineOf(source: string, index: number): number {
  return source.slice(0, index).split('\n').length;
}
